import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ErrorBox, Logo } from "@/components/Bits";

const Shell = ({ title, sub, children }) => (
  <div className="atmosphere min-h-[80vh] flex items-center justify-center px-4 py-12" style={{ "--atm-bg": "#EFE2D0" }}>
    <div className="card w-full max-w-md p-7 sm:p-9 bg-[#FFFDF9] fade-up">
      <div className="mb-6"><Logo /></div>
      <h1 className="text-3xl text-[#29483D]">{title}</h1>
      {sub && <p className="text-sm text-stone-600 mt-2">{sub}</p>}
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const destFor = (u) => (u.active_mode === "admin" || u.active_mode === "super_admin" ? "/admin" : u.active_mode === "reviewer" ? "/reviewer" : "/dashboard");

export function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate(); const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" }); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", f); setUser(data.user); nav(sp.get("next") || destFor(data.user)); }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Log in to your VyapaarSetu account.">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="lbl" htmlFor="email">Email</label><input id="email" type="email" autoComplete="email" required className="inp" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div><label className="lbl" htmlFor="password">Password</label><input id="password" type="password" autoComplete="current-password" required className="inp" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        <ErrorBox msg={err} />
        <button className="btn-primary w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Logging in…" : "Log in"}</button>
        <div className="flex justify-between text-sm"><Link to="/forgot-password" className="text-[#B86545]" data-testid="forgot-password-link">Forgot password?</Link><Link to="/signup" className="text-[#29483D] font-semibold">Create account</Link></div>
      </form>
    </Shell>
  );
}

export function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate(); const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", role: sp.get("role") === "seller" ? "seller" : "buyer" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", f); setUser(data.user);
      const s = data.email_status;
      if (s === "sent") toast.success("Account created. Check your inbox to verify your email.");
      else if (s === "not_configured") toast.warning("Account created. Email is not configured, so no verification email was sent.");
      else if (s === "skipped_reserved_domain") toast.warning("Account created. Test email domains do not receive emails.");
      else toast.warning("Account created, but the verification email could not be sent. You can resend it from your account.");
      nav(f.role === "seller" ? "/dashboard/listings" : "/dashboard/onboarding");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" sub="One account works for buying and selling — switch roles any time.">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#E9DDCB]" role="radiogroup" aria-label="I want to">
          {[["buyer", "Buy a business"], ["seller", "Sell a business"]].map(([v, t]) => <button type="button" key={v} role="radio" aria-checked={f.role === v} onClick={() => setF({ ...f, role: v })} className={`rounded-full py-2 text-sm font-semibold ${f.role === v ? "bg-[#29483D] text-white" : "text-[#49392F]"}`} data-testid={`signup-role-${v}`}>{t}</button>)}
        </div>
        <div><label className="lbl" htmlFor="name">Full name</label><input id="name" required minLength={2} className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="signup-name-input" /></div>
        <div><label className="lbl" htmlFor="semail">Email</label><input id="semail" type="email" required className="inp" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="signup-email-input" /></div>
        <div><label className="lbl" htmlFor="spass">Password</label><input id="spass" type="password" required minLength={8} autoComplete="new-password" className="inp" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="signup-password-input" /><p className="text-[11px] text-stone-500 mt-1">At least 8 characters.</p></div>
        <ErrorBox msg={err} />
        <button className="btn-primary w-full" disabled={busy} data-testid="signup-submit-btn">{busy ? "Creating…" : "Create account"}</button>
        <p className="text-xs text-stone-500">By continuing you agree to the <Link to="/page/terms" className="underline">draft terms</Link> and <Link to="/page/privacy" className="underline">privacy policy</Link>.</p>
        <p className="text-sm">Already registered? <Link to="/login" className="text-[#B86545] font-semibold">Log in</Link></p>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState(""); const [res, setRes] = useState(null); const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setErr(""); try { const { data } = await api.post("/auth/forgot-password", { email }); setRes(data); } catch (e2) { setErr(errMsg(e2)); } };
  return (
    <Shell title="Reset your password" sub="We'll email you a single-use link that expires in one hour.">
      {res ? <div className="space-y-2" data-testid="forgot-result"><p className="text-sm">{res.message}</p>{!res.email_configured && <p className="text-xs text-[#9B3B22]">Email delivery is not configured on this deployment, so no email can be sent.</p>}</div> : (
        <form onSubmit={submit} className="space-y-4">
          <div><label className="lbl" htmlFor="femail">Email</label><input id="femail" type="email" required className="inp" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          <ErrorBox msg={err} /><button className="btn-primary w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const [sp] = useSearchParams(); const nav = useNavigate();
  const [pw, setPw] = useState(""); const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setErr(""); try { await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw }); toast.success("Password updated. Please log in."); nav("/login"); } catch (e2) { setErr(errMsg(e2)); } };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="lbl" htmlFor="npw">New password</label><input id="npw" type="password" minLength={8} required className="inp" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
        <ErrorBox msg={err} /><button className="btn-primary w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams(); const { refresh } = useAuth();
  const [state, setState] = useState("working");
  useEffect(() => { api.post("/auth/verify-email", { token: sp.get("token") || "" }).then(() => { setState("ok"); refresh(); }).catch(() => setState("bad")); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Shell title={state === "ok" ? "Email verified" : state === "bad" ? "Link not valid" : "Verifying…"}>
      <p className="text-sm" data-testid="verify-email-status">{state === "ok" ? "Thank you — your email address is verified." : state === "bad" ? "This link is invalid or expired. Request a new one from your account page." : "Please wait."}</p>
      <Link to="/dashboard" className="btn-primary mt-5">Go to dashboard</Link>
    </Shell>
  );
}
